import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { type RowSelectionState } from "@tanstack/react-table";

import { usePagination } from "@vega-api/custom/utils/usePagination";
import { useGetDealProspectsPermissionView } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import { useGetDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import { useGetDealGeneralPartnerViewV3 } from "@vega-api/generated/deal/deal";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useClipboard } from "@vega-core/hooks/useClipboard";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";

export type ProspectWithMemberCount = DealShortlistItemResponse & {
  memberCount?: number;
};

export const useOutreachManagement = () => {
  const { id: dealIdFromParams } = useParams<{ id: string }>();
  const dealId = dealIdFromParams ?? "";

  const {
    isOpen: isShareModalOpen,
    setTrue: openShareModal,
    setFalse: closeShareModal,
  } = useBoolean(false);
  const {
    isOpen: isOpportunityOwnerModalOpen,
    setTrue: openOpportunityOwnerModal,
    setFalse: closeOpportunityOwnerModal,
  } = useBoolean(false);
  const {
    isOpen: isUpdateDataPointsModalOpen,
    setTrue: openUpdateDataPointsModal,
    setFalse: closeUpdateDataPointsModal,
  } = useBoolean(false);
  const {
    isOpen: isContactsModalOpen,
    setTrue: openContactsModal,
    setFalse: closeContactsModal,
  } = useBoolean(false);
  const {
    isOpen: isEmailEditModalOpen,
    setTrue: openEmailEditModal,
    setFalse: closeEmailEditModal,
  } = useBoolean(false);
  const {
    isOpen: isCopyLinkNoticeOpen,
    setTrue: showCopyLinkNotice,
    setFalse: dismissCopyLinkNotice,
  } = useBoolean(false);

  const [selectedProspectForShare, setSelectedProspectForShare] =
    useState<ProspectWithMemberCount | null>(null);
  const [selectedProspectsForEmailEdit, setSelectedProspectsForEmailEdit] =
    useState<DealShortlistItemResponse[]>([]);
  const [selectedProspectForContacts, setSelectedProspectForContacts] =
    useState<DealShortlistItemResponse | null>(null);
  const [
    selectedProspectIdsForOpportunityOwner,
    setSelectedProspectIdsForOpportunityOwner,
  ] = useState<string[]>([]);
  const [
    dealOpportunityIdForEditingDataPoints,
    setDealOpportunityIdForEditingDataPoints,
  ] = useState<string | null>(null);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const paginationState = usePaginationState();

  const { copy } = useClipboard({
    name: m.coInvestments_outreach_link(),
    withToast: true,
  });

  const { data: permissionViewData } = useGetDealProspectsPermissionView(
    dealId,
    {
      query: {
        enabled: !!dealId,
        select: (response) => {
          const limitedPartners = response.data?.limitedPartners || [];

          return limitedPartners.reduce(
            (acc, lp) => {
              acc[lp.limitedPartner.id] = lp.memberCount;

              return acc;
            },
            {} as Record<string, number>,
          );
        },
      },
    },
  );

  const { data: shortlistResponse, isPending: isLoadingShortlist } =
    useGetDealShortlist(
      dealId,
      {
        isApproved: true,
        page: paginationState.currentPage,
        pageSize: paginationState.currentPageSize,
      },
      {
        query: {
          enabled: !!dealId,
        },
      },
    );

  const { paginationElement } = usePagination({
    meta: shortlistResponse?.meta ?? null,
    isLoading: isLoadingShortlist,
    filterDependencies: [],
    ...paginationState,
  });

  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId },
    {
      query: {
        enabled: !!dealId,
        select: (response) => response.data,
      },
    },
  );
  const canEditDealOpportunity = permissions?.canEditDealOpportunity;

  const { data: dealData } = useGetDealGeneralPartnerViewV3(dealId, {
    query: {
      enabled: !!dealId,
      select: (data) => data.data,
    },
  });

  const dealName = dealData?.deal.name ?? "";

  const prospectsData: ProspectWithMemberCount[] = useMemo(() => {
    const items = shortlistResponse?.data?.dealShortlistItems ?? [];

    return items.map((prospect) => ({
      ...prospect,
      memberCount: permissionViewData?.[prospect.limitedPartner.id] ?? 0,
    }));
  }, [shortlistResponse?.data?.dealShortlistItems, permissionViewData]);

  const handleShare = (rowIndex: number) => {
    const prospect = prospectsData[rowIndex];
    setSelectedProspectForShare(prospect);
    openShareModal();
  };

  const handleCloseShareModal = () => {
    closeShareModal();
    setSelectedProspectForShare(null);
  };

  const handleEditEmail = (prospectIds: string[]) => {
    const prospects = prospectIds.flatMap((id) => {
      const prospect = prospectsData.find((p) => p.id === id);

      return prospect ? [prospect] : [];
    });

    setSelectedProspectsForEmailEdit(prospects);
    openEmailEditModal();
  };

  const canAllSelectedProspectsSendDeal = Object.keys(rowSelection).every(
    (prospectId) => {
      const prospect = prospectsData.find((p) => p.id === prospectId);

      if (!prospect) return false;
      const {
        canSendDeal,
        relationship,
        isDealSent,
        memberCount = 0,
      } = prospect;

      return canSendDeal && relationship && !isDealSent && memberCount > 0;
    },
  );

  const selectedCount = Object.keys(rowSelection).length;

  const handleCopyLink = async (rowIndex: number) => {
    const prospect = prospectsData[rowIndex];
    copy(prospect.dealLink);
    showCopyLinkNotice();
  };

  const handleAddOpportunityOwner = (prospectIds: string[]) => {
    setSelectedProspectIdsForOpportunityOwner(prospectIds);
    openOpportunityOwnerModal();
  };

  const selectedProspectsForOpportunityOwner =
    selectedProspectIdsForOpportunityOwner.flatMap((id) => {
      const prospect = prospectsData.find((p) => p.id === id);

      return prospect ? [prospect] : [];
    });

  const handleUpdateDataPoints = canEditDealOpportunity
    ? (rowIndex: number) => {
        const prospect = prospectsData[rowIndex];
        setDealOpportunityIdForEditingDataPoints(prospect.dealOpportunityId);
        openUpdateDataPointsModal();
      }
    : undefined;

  const handleOpenContactsModal = (rowIndex: number) => {
    const prospect = prospectsData[rowIndex];
    setSelectedProspectForContacts(prospect);
    openContactsModal();
  };

  const handleCloseContactsModal = () => {
    closeContactsModal();
    setSelectedProspectForContacts(null);
  };

  return {
    dealId,
    dealName,
    prospectsData,
    paginationElement,
    isLoading: isLoadingShortlist,
    isShareModalOpen,
    selectedProspectForShare,
    handleShare,
    handleCloseShareModal,
    isEmailEditModalOpen,
    selectedProspectsForEmailEdit,
    handleEditEmail,
    closeEmailEditModal,
    canAllSelectedProspectsSendDeal,
    handleCopyLink,
    handleAddOpportunityOwner,
    rowSelection,
    selectedCount,
    isOpportunityOwnerModalOpen,
    selectedProspectsForOpportunityOwner,
    closeOpportunityOwnerModal,
    isUpdateDataPointsModalOpen,
    closeUpdateDataPointsModal,
    dealOpportunityIdForEditingDataPoints,
    handleUpdateDataPoints,
    setRowSelection,
    isContactsModalOpen,
    selectedProspectForContacts,
    handleOpenContactsModal,
    handleCloseContactsModal,
    isCopyLinkNoticeOpen,
    dismissCopyLinkNotice,
  };
};
